<template>
  <div class="ws-podcast-player">
    
    <CrossFadeTransition :show="visible">
      <div class="ws-podcast-player--inner md:p-1 grid items-baseline-last relative">
        <!-- <button
          @click="closePlayer"
          class="hidden md:flex absolute top-[5px] right-[20px] text-gray-500 hover:text-red-600 text-xl"
          aria-label="Close Player"
        >
          ✖
        </button> -->
        <!-- <div v-if="currentTrack.cover" class="ws-media-cover">
          <NuxtImg :src="currentTrack.cover" :alt="currentTrack.title" class="ws-podcast-cover"></NuxtImg>
        </div> -->
        <div class="ws-media-controls">
          <div class="controls-container px-1 md:px-0 flex items-center md:items-end">
            <div class="track-box">
              <div class="ws-podcast--title text-green">
                {{ currentTrack.title }}
              </div>
              <div class="ws-podcast--subtitle text-white">
                {{ currentTrack.subtitle }}
              </div>
            </div>
            <div class="controls">
              <button @click="prevTrack">
                <font-awesome-icon icon="fa-solid fa-backward text-white" />
              </button>
              <button @click="togglePlay" class="play-button">
                <span v-if="isBuffering">
                  <font-awesome-icon icon="fa-solid fa-spinner text-green" />
                </span>
                <span v-else>
                  <font-awesome-icon icon="fa-solid fa-pause text-green" v-if="isPlaying" />
                  <font-awesome-icon icon="fa-solid fa-play text-green" v-else />
                </span>
              </button>
              <button @click="nextTrack">
                <font-awesome-icon icon="fa-solid fa-forward text-white" />
              </button>
            </div>
          </div>

          <div class="equalizer-bar hidden md:flex" v-show="isPlaying && !isBuffering">
            <span v-for="n in 20" :key="n" :style="{ '--i': n }"></span>
          </div>

          <div class="progress-container" @click="!isBuffering && seek($event)">
            <div v-if="isBuffering" class="buffering-bar animate-pulse"></div>
            <div v-else class="progress" :style="{ width: progress + '%' }"></div>
          </div>
        </div>

        <audio
          ref="audio"
          @timeupdate="onTimeUpdate"
          @ended="onEnded"
          @waiting="() => onBuffering(true)"
          @playing="() => onBuffering(false)"
          @canplay="onCanPlay"
          @stalled="() => onBuffering(true)"
        />
      </div>
    </CrossFadeTransition>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useMedia } from '@/stores/media'

const store = useMedia()
const { tracks, currentIndex, isPlaying, isBuffering, isVisible } = storeToRefs(store)

const audio = ref<HTMLAudioElement | null>(null)
const progress = ref(0)
const canPlay = ref(false)

const visible = computed(() => isVisible.value)
const currentTrack = computed(() => tracks.value[currentIndex.value] || { title: '', subtitle: '', src: '' })

const closePlayer = () => {
  if (audio.value) {
    audio.value.pause()
    audio.value.src = ''
  }
  store.setAudioEl(null)
  store.setPlaying(false)
  store.setBuffering(false)
  progress.value = 0
  canPlay.value = false
  store.setVisibility(false)
}

watch([currentIndex, tracks], () => {
  if (!audio.value) return
  canPlay.value = false
  audio.value.src = currentTrack.value.src
  audio.value.load()

  if (store.isPlaying) {
    audio.value.play().catch(e => console.warn('Play-Fehler beim Trackwechsel:', e))
  }
})

onMounted(() => {
  if (audio.value) {
    store.setAudioEl(audio.value)
    audio.value.src = currentTrack.value.src

    audio.value.addEventListener('canplay', () => {
      canPlay.value = true
      store.setBuffering(false)
      if (store.isPlaying) {
        audio.value?.play().catch(e => console.warn('Play-Fehler:', e))
      }
    })
  }

  if ('mediaSession' in navigator) {
    navigator.mediaSession.setActionHandler('play', () => store.setPlaying(true))
    navigator.mediaSession.setActionHandler('pause', () => store.setPlaying(false))
    navigator.mediaSession.setActionHandler('previoustrack', () => store.prev())
    navigator.mediaSession.setActionHandler('nexttrack', () => store.next())
  }
})

watch(isPlaying, (play) => {
  if (!audio.value) return
  if (play) {
    if (canPlay.value) {
      audio.value.play().catch(e => console.warn('Play-Fehler:', e))
    }
  } else {
    audio.value.pause()
  }
})

watch(currentTrack, (track) => {
  if ('mediaSession' in navigator && track?.title) {
    navigator.mediaSession.metadata = new MediaMetadata({
      title: track.title,
      artist: track.subtitle,
      album: 'Podcast',
      artwork: [
        {
          src: 'https://source.unsplash.com/96x96/?music',
          sizes: '96x96',
          type: 'image/png'
        }
      ]
    })
  }
})

const togglePlay = () => {
  if (!store.isVisible) {
    // Player sichtbar machen
    store.setVisibility(true)
  }
  if (!canPlay.value) {
    store.setPlaying(true)
  } else {
    store.togglePlay()
  }
}

const nextTrack = () => {
  store.next()
}

const prevTrack = () => {
  store.prev()
}

const onBuffering = (buffering: boolean) => {
  store.setBuffering(buffering)
}

const onCanPlay = () => {
  canPlay.value = true
  if (store.isPlaying && audio.value) {
    audio.value.play().catch(e => console.warn('Play-Fehler:', e))
  }
  store.setBuffering(false)
}

const onTimeUpdate = () => {
  if (audio.value && audio.value.duration) {
    progress.value = (audio.value.currentTime / audio.value.duration) * 100
  }
}

const seek = (event: MouseEvent) => {
  const container = event.currentTarget as HTMLElement
  const rect = container.getBoundingClientRect()
  const clickX = event.clientX - rect.left
  const width = container.clientWidth
  const duration = audio.value?.duration ?? 0

  if (audio.value && duration) {
    audio.value.currentTime = (clickX / width) * duration
  }
}

const onEnded = () => {
  if (currentIndex.value + 1 < tracks.value.length) {
    store.next()
  } else {
    store.setPlaying(false)
  }
}
</script>

<style scoped>
.progress-container {
  height: 6px;
  background: #999;
  overflow: hidden;
  cursor: pointer;
  margin-top: 8px;
}
.progress {
  height: 100%;
  background: var(--color-green);
  transition: width 0.2s ease;
}
.buffering-bar {
  width: 100%;
  height: 6px;
  background: repeating-linear-gradient(
    90deg,
    #10b981,
    #10b981 10px,
    #ccc 10px,
    #ccc 20px
  );
}

/* 🎛️ Equalizer Bar unter der Progressbar */
.equalizer-bar {
  /* display: flex; */
  justify-content: space-between;
  align-items: flex-end;
  height: 16px;
  margin-top: 16px;
  padding: 0 4px;
}

.equalizer-bar span {
  width: 4px;
  background-color: var(--color-green);
  animation: equalizerBounce 1s infinite ease-in-out;
  border-radius: 2px;
  animation-delay: calc(var(--i) * 0.05s);
}

@keyframes equalizerBounce {
  0%, 100% {
    height: 20%;
  }
  50% {
    height: 100%;
  }
}
</style>
